import React, { useEffect, useState } from 'react';
import client, { apiError } from '../api/client';

const TABS = ['Overview', 'Users', 'Content', 'Retention'];

export default function AdminDashboard() {
  const [tab, setTab] = useState('Overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [content, setContent] = useState([]);
  const [retention, setRetention] = useState(null);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const [form, setForm] = useState({ title: '', domain: 'all', type: 'syllabus', bodyText: '', tags: '' });

  const loadOverview = async () => {
    try { const { data } = await client.get('/admin/stats'); setStats(data); }
    catch (err) { setError(apiError(err)); }
  };
  const loadUsers = async () => {
    try { const { data } = await client.get('/admin/users'); setUsers(data.users); }
    catch (err) { setError(apiError(err)); }
  };
  const loadContent = async () => {
    try { const { data } = await client.get('/content'); setContent(data.items); }
    catch (err) { setError(apiError(err)); }
  };
  const loadRetention = async () => {
    try { const { data } = await client.get('/admin/retention/review'); setRetention(data); }
    catch (err) { setError(apiError(err)); }
  };

  useEffect(() => {
    setError(''); setMsg('');
    if (tab === 'Overview') loadOverview();
    if (tab === 'Users') loadUsers();
    if (tab === 'Content') loadContent();
    if (tab === 'Retention') loadRetention();
  }, [tab]);

  const setStatus = async (id, status) => {
    setError(''); setMsg('');
    try { await client.patch(`/admin/users/${id}/status`, { status }); setMsg('Status updated.'); loadUsers(); }
    catch (err) { setError(apiError(err)); }
  };

  const createContent = async (e) => {
    e.preventDefault(); setError(''); setMsg('');
    try {
      await client.post('/content', {
        ...form,
        tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      });
      setMsg('Content added and indexed for chat.');
      setForm({ title: '', domain: 'all', type: 'syllabus', bodyText: '', tags: '' });
      loadContent();
    } catch (err) { setError(apiError(err)); }
  };

  const deleteContent = async (id) => {
    setError(''); setMsg('');
    try { await client.delete(`/content/${id}`); loadContent(); }
    catch (err) { setError(apiError(err)); }
  };

  return (
    <div className="container" style={{ marginTop: 28, marginBottom: 40 }}>
      <h1>Admin</h1>
      <div className="row wrap" style={{ margin: '14px 0' }}>
        {TABS.map((t) => (
          <button key={t} className={`btn btn-sm ${tab === t ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
      </div>
      {error && <div className="alert alert-error" style={{ marginBottom: 14 }}>{error}</div>}
      {msg && <div className="alert alert-ok" style={{ marginBottom: 14 }}>{msg}</div>}

      {tab === 'Overview' && stats && (
        <div className="grid-3">
          <div className="card"><h3>{stats.totalUsers}</h3><p className="muted small">Total users</p></div>
          <div className="card"><h3>{stats.verified}</h3><p className="muted small">Email-verified</p></div>
          <div className="card"><h3>{stats.contentCount}</h3><p className="muted small">Indexed content items</p></div>
          <div className="card">
            <h3>By status</h3>
            {stats.byStatus.map((s) => (
              <div key={s.credentialStatus} className="row-between small"><span>{s.credentialStatus}</span><b>{s.count}</b></div>
            ))}
          </div>
          <div className="card">
            <h3>By source</h3>
            {stats.bySource.map((s, i) => (
              <div key={i} className="row-between small"><span>{s.distributionSource || '—'}</span><b>{s.count}</b></div>
            ))}
          </div>
        </div>
      )}

      {tab === 'Users' && (
        <div className="card">
          <table>
            <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td className="small">{u.fullName}</td>
                  <td className="small">{u.email}</td>
                  <td className="small">{u.role}</td>
                  <td><span className={`status ${u.credentialStatus}`}>{u.credentialStatus}</span></td>
                  <td>
                    {u.role !== 'superadmin' && (
                      <div className="row wrap">
                        <button className="btn btn-ghost btn-sm" onClick={() => setStatus(u.id, 'active')}>Activate</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setStatus(u.id, 'suspended')}>Suspend</button>
                        <button className="btn btn-danger btn-sm" onClick={() => setStatus(u.id, 'revoked')}>Revoke</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'Content' && (
        <div className="grid-2">
          <div className="card">
            <h3>Add content</h3>
            <p className="tiny muted">Text you add here is indexed and becomes retrievable in chat.</p>
            <form onSubmit={createContent} style={{ marginTop: 10 }}>
              <div className="field"><label>Title</label><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></div>
              <div className="field">
                <label>Domain</label>
                <select value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })}>
                  <option value="all">All</option>
                  <option value="people">People</option>
                  <option value="process">Process</option>
                  <option value="business-environment">Business Environment</option>
                </select>
              </div>
              <div className="field">
                <label>Type</label>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  <option value="syllabus">Syllabus</option>
                  <option value="practice-questions">Practice questions</option>
                  <option value="recommended-readings">Recommended readings</option>
                  <option value="mock-exams">Mock exams</option>
                </select>
              </div>
              <div className="field"><label>Body text</label><textarea rows={6} value={form.bodyText} onChange={(e) => setForm({ ...form, bodyText: e.target.value })} /></div>
              <div className="field"><label>Tags (comma-separated)</label><input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} /></div>
              <button className="btn btn-primary btn-block" style={{ marginTop: 14 }}>Add &amp; index</button>
            </form>
          </div>
          <div className="card">
            <h3>Indexed content ({content.length})</h3>
            <div className="stack" style={{ maxHeight: 520, overflowY: 'auto', marginTop: 8 }}>
              {content.map((c) => (
                <div key={c.id} className="row-between" style={{ borderBottom: '1px solid var(--line)', paddingBottom: 8 }}>
                  <div>
                    <div className="small" style={{ fontWeight: 600 }}>{c.title}</div>
                    <div className="tiny muted">{c.domain} · {c.type}</div>
                  </div>
                  <button className="btn btn-danger btn-sm" onClick={() => deleteContent(c.id)}>Delete</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'Retention' && retention && (
        <div className="card">
          <div className="alert alert-info" style={{ marginBottom: 14 }}>{retention.note}</div>
          {retention.accounts.length === 0 ? (
            <p className="muted small">No accounts are currently past the recoverable window.</p>
          ) : (
            <table>
              <thead><tr><th>Name</th><th>Email</th><th>Status</th><th>Since</th></tr></thead>
              <tbody>
                {retention.accounts.map((a) => (
                  <tr key={a.id}>
                    <td className="small">{a.fullName}</td>
                    <td className="small">{a.email}</td>
                    <td><span className={`status ${a.credentialStatus}`}>{a.credentialStatus}</span></td>
                    <td className="tiny muted">{a.statusChangedAt ? new Date(a.statusChangedAt).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
